var io = require('../libs/socketio');

// A newly configured gateway can take one worker refresh interval (30s) to
// join the durable write scheduler. Leave enough room for that refresh plus
// the device write timeout without slowing successful confirmations.
var DEFAULT_TIMEOUT_MS = 45000;
var DEFAULT_REPLAY_POLL_MS = 250;
var DEFAULT_RECONCILIATION_ATTEMPTS = 3;

function normalizeRid(rid) {
  if (rid && typeof rid === 'object') {
    rid = rid['@rid'];
  }
  if (typeof rid !== 'string' || !rid) {
    return null;
  }
  return rid[0] === '#' ? rid : '#' + rid;
}

function normalizeRidList(pointRids) {
  return (Array.isArray(pointRids) ? pointRids : [pointRids])
    .map(normalizeRid)
    .filter(function (rid) { return !!rid; });
}

function eventMatchesPoint(data, pointRids) {
  var eventRid = normalizeRid(data && data['@rid']);
  return !!eventRid && pointRids.indexOf(eventRid) !== -1;
}

function eventMatchesCommand(data, commandRid) {
  if (!commandRid) return false;
  return !!(data && data.commandRid && String(data.commandRid) === String(commandRid));
}

function commandError(data) {
  var message = (data && data.error) || 'Write failed';
  var error = new Error(message);
  error.event = data;
  return error;
}

function commandExecution(result) {
  return result && result.writeOperation && result.writeOperation.commandExec || null;
}

function commandExecutionError(result, exec) {
  var state = String(exec && exec.state || '').toLowerCase();
  var error = new Error(
    exec && exec.error ||
    (state === 'queue-ambiguous'
      ? 'The write queue outcome is uncertain and requires reconciliation.'
      : 'Write failed')
  );
  error.command = result;
  error.event = exec || null;
  error.writeOperation = Object.assign({}, result && result.writeOperation || {}, {
    retrySafe: !!(exec && exec.retrySafe === true &&
      String(exec.deviceDisposition || '').toLowerCase() === 'not-invoked'),
    deviceDisposition: exec && exec.deviceDisposition || 'unknown'
  });
  return error;
}

function requestErrorStatus(error) {
  var candidates = [
    error && error.status,
    error && error.statusCode,
    error && error.code,
    error && error.response && error.response.status,
    error && error.response && error.response.statusCode
  ];
  for (var index = 0; index < candidates.length; index += 1) {
    var candidate = Number(candidates[index]);
    if (Number.isFinite(candidate)) return candidate;
  }
  return null;
}

function structuredErrorPayload(error) {
  var candidates = [
    error,
    error && error.response,
    error && error.response && error.response.data,
    error && error.data
  ];
  for (var index = 0; index < candidates.length; index += 1) {
    var candidate = candidates[index];
    if (!candidate || typeof candidate !== 'object') continue;
    if (candidate.writeOperation || candidate.fields ||
        typeof candidate.error === 'string') {
      return candidate;
    }
    if (typeof candidate.code === 'string' &&
        !/^(?:ECONN|ETIMEDOUT|ERR_NETWORK|NETWORK_)/i.test(candidate.code)) {
      return candidate;
    }
  }
  return null;
}

function isTransientRequestError(error) {
  // A response carrying the server's authorization, validation, or durable
  // operation result is authoritative. Re-sending it only delays useful
  // feedback and can obscure retrySafe/deviceDisposition metadata.
  if (structuredErrorPayload(error)) return false;

  var status = requestErrorStatus(error);
  if (status !== null) {
    return status === 0 || status === 408 || status === 429 || status >= 500;
  }

  var code = error && error.code;
  if (typeof code === 'string') {
    return /^(?:ECONN|ETIMEDOUT|ERR_NETWORK|NETWORK_)/i.test(code);
  }

  // Mithril reports a lost XHR response as an Error without a response body.
  // The exact frozen request can safely be used to query the same operation.
  return error instanceof Error;
}

function waitForPointWrite(options) {
  options = options || {};
  var pointRids = normalizeRidList(options.pointRids || options.pointRid);
  var request = options.request;
  var timeoutMs = Number(options.timeoutMs) || DEFAULT_TIMEOUT_MS;
  var timeoutMessage = options.timeoutMessage || 'Timed out waiting for write confirmation.';
  var onCommand = options.onCommand;
  var replayPollMs = Number(options.replayPollMs) || DEFAULT_REPLAY_POLL_MS;
  var reconciliationAttempts = Object.prototype.hasOwnProperty.call(
    options,
    'reconciliationAttempts'
  ) ? Math.floor(Number(options.reconciliationAttempts)) : DEFAULT_RECONCILIATION_ATTEMPTS;
  if (!Number.isFinite(reconciliationAttempts) || reconciliationAttempts < 0) {
    reconciliationAttempts = DEFAULT_RECONCILIATION_ATTEMPTS;
  }

  if (!pointRids.length) {
    return Promise.reject(new Error('Cannot wait for write confirmation without a point RID.'));
  }
  if (typeof request !== 'function') {
    return Promise.reject(new Error('Cannot wait for write confirmation without a request function.'));
  }

  return new Promise(function (resolve, reject) {
    var commandRid = null;
    var commandResult = null;
    var commandReady = false;
    var settled = false;
    var onCommandApplied = false;
    var pendingEvents = [];
    var pollTimer = null;
    var reconciliationAttemptsUsed = 0;
    var socketReconciliationStarted = false;
    var socketReconciliationError = null;
    var timer = setTimeout(function () {
      finish(reject, new Error(timeoutMessage));
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timer);
      if (pollTimer) clearTimeout(pollTimer);
      io.off('point-write', handlePointWrite);
      io.off('point-write-error', handlePointWriteError);
    }

    function finish(fn, value) {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    }

    function handleEvent(data, isError) {
      if (!eventMatchesPoint(data, pointRids)) return;
      if (!commandReady) {
        pendingEvents.push({ data: data, isError: isError });
        return;
      }
      if (!eventMatchesCommand(data, commandRid)) return;
      if (isError) {
        // The worker persists its terminal Command state before emitting this
        // event. Query the same writeOperationId so callers receive the
        // durable retrySafe/deviceDisposition result instead of a lossy
        // socket-only error. This request never creates a new operation.
        if (!socketReconciliationStarted) {
          socketReconciliationStarted = true;
          socketReconciliationError = commandError(data);
          scheduleReconciliation(socketReconciliationError, true);
        }
      } else {
        finish(resolve, {
          command: commandResult,
          event: data
        });
      }
    }

    function handlePointWrite(data) {
      handleEvent(data, false);
    }

    function handlePointWriteError(data) {
      handleEvent(data, true);
    }

    function replayPendingEvents() {
      pendingEvents.slice().some(function (entry) {
        if (!eventMatchesCommand(entry.data, commandRid)) return false;
        handleEvent(entry.data, entry.isError);
        return true;
      });
    }

    io.on('point-write', handlePointWrite);
    io.on('point-write-error', handlePointWriteError);

    function scheduleReconciliation(fallbackError, immediate) {
      if (reconciliationAttemptsUsed >= reconciliationAttempts) {
        finish(reject, fallbackError);
        return false;
      }
      reconciliationAttemptsUsed += 1;
      pollTimer = setTimeout(requestOnce, immediate ? 0 : replayPollMs);
      return true;
    }

    function applyCommandResult(result) {
      if (settled) return;
      var operation = result && result.writeOperation;
      if (operation && operation.pending === true) {
        pollTimer = setTimeout(requestOnce, replayPollMs);
        return;
      }

      commandResult = result;
      commandRid = result && result.commandRid;
      if (!commandRid) {
        finish(reject, new Error('Write was queued without a command id.'));
        return;
      }
      var exec = commandExecution(result);
      var execState = String(exec && exec.state || '').toLowerCase();
      var commandHook = onCommandApplied || typeof onCommand !== 'function'
        ? Promise.resolve()
        : Promise.resolve(onCommand(result)).then(function () {
          onCommandApplied = true;
        });
      return commandHook.then(function () {
        if (execState === 'sent') {
          finish(resolve, {
            command: result,
            event: Object.assign({
              '@rid': pointRids[0],
              commandRid: commandRid,
              replayed: true
            }, exec)
          });
          return;
        }
        if (execState === 'error' || execState === 'timeout' ||
            execState === 'queue-ambiguous') {
          finish(reject, commandExecutionError(result, exec));
          return;
        }
        if (socketReconciliationStarted) {
          // A socket failure and a still-nonterminal replay disagree. Keep
          // checking the same durable operation within the explicit bound;
          // never reinterpret this as permission to issue a new request id.
          scheduleReconciliation(socketReconciliationError, false);
          return;
        }
        commandReady = true;
        replayPendingEvents();
      });
    }

    function requestOnce() {
      if (settled) return;
      pollTimer = null;
      Promise.resolve()
      .then(request)
      .then(applyCommandResult, function (error) {
        if (settled) return;
        if (isTransientRequestError(error) &&
            scheduleReconciliation(error, false)) {
          return;
        }
        if (!settled) finish(reject, error);
      })
      .catch(function (error) {
        // Errors raised while applying the server response (including a
        // caller's onCommand hook) are not HTTP transport failures.
        if (!settled) finish(reject, error);
      });
    }

    requestOnce();
  });
}

module.exports = {
  waitForPointWrite: waitForPointWrite,
  _test: {
    normalizeRid: normalizeRid,
    eventMatchesPoint: eventMatchesPoint,
    eventMatchesCommand: eventMatchesCommand,
    commandExecution: commandExecution,
    commandExecutionError: commandExecutionError,
    requestErrorStatus: requestErrorStatus,
    structuredErrorPayload: structuredErrorPayload,
    isTransientRequestError: isTransientRequestError,
    DEFAULT_RECONCILIATION_ATTEMPTS: DEFAULT_RECONCILIATION_ATTEMPTS
  }
};
